import ReactGA from "react-ga4";
import UAParser from "ua-parser-js";
import { parseAnalyticsParams } from "./helpers";
import TagManager from "react-gtm-module";
import { v4 as uuidv4 } from "uuid";

const parser = new UAParser();
const ENVIRONMENT = process.env.REACT_APP_ENV || "development";
export const TRACKING_ID = ENVIRONMENT === "prod" ? "G-L6PJ0P400Z" : "G-GQJS32GLKL";

const getAppEnvironment = (): string => {
  if (ENVIRONMENT === "prod") return "prod";
  if (ENVIRONMENT === "staging") return "stag";
  return "dev";
};

/* ------------------------------------------------------------------ */
/*  Tag Manager initialization                                        */
/* ------------------------------------------------------------------ */
export const initTagManager = () => {
  const gtmId = process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID;

  // Only initialize if GTM ID is provided
  if (!gtmId) {
    console.warn("Google Tag Manager ID not configured. Skipping GTM initialization.");
    return;
  }

  TagManager.initialize({
    gtmId,
    auth: process.env.REACT_APP_GOOGLE_TAG_MANAGER_AUTH,
    preview: process.env.REACT_APP_GOOGLE_TAG_MANAGER_PREVIEW,
  });
};

/* ------------------------------------------------------------------ */
/*  Persistent identifiers                                            */
/* ------------------------------------------------------------------ */

/** Unique browser/device ID (persists across sessions) */
export const getDeviceId = (): string => {
  const existing = localStorage.getItem("device_id");
  if (existing) return existing;

  const newId = uuidv4();
  localStorage.setItem("device_id", newId);
  return newId;
};

export const PLATFORM_TYPE = process.env.REACT_APP_PLATFORM_TYPE || "web";

const enrichParams = (params: Record<string, unknown> = {}) => ({
  device_id: getDeviceId(),
  device_type: parser.getOS().name,
  platform_type: PLATFORM_TYPE,
  app_platform: "tradegpt",
  app_environment: getAppEnvironment(),
  user_type: "signed_user",
  ...parseAnalyticsParams(params),
});

/* ------------------------------------------------------------------ */
/*  Internal state and queue                                          */
/* ------------------------------------------------------------------ */
const eventQueue: Record<string, unknown>[] = [];
let authReady = false;

/** Flush queued events once user is identified */
const flushQueue = (): void => {
  if (!eventQueue.length) return;
  eventQueue.forEach(payload => safePush(payload));
  eventQueue.length = 0;
};

/* ------------------------------------------------------------------ */
/*  Core tracking methods                                             */
/* ------------------------------------------------------------------ */

const safePush = (data: Record<string, unknown>) => {
  try {
    TagManager.dataLayer({ dataLayer: data });
  }
  catch (err) {
    console.warn("GTM push blocked:", err);
  }
};

/** Identify current user and flush queued events */
export const identifyUser = (userId: string): void => {
  authReady = true;
  ReactGA.set({ user_id: userId });
  ReactGA.gtag("set", {
    user_properties: {
      crm_id: userId,
    },
  });
  safePush(enrichParams({ event: "user_identified", crm_id: userId }));
  flushQueue();
};

/** Generic event logger (queues until user is known) */
export const logEvent = (event: string, params: Record<string, unknown> = {}): void => {
  const payload = { event, ...enrichParams(params) };

  if (authReady) {
    safePush(payload);
  }
  else {
    eventQueue.push(payload);
  }
};

export const logPageView = (path: string, params: Record<string, unknown> = {}): void => {
  try {
    logEvent("page_view", {
      page_location: path,
      page_referrer: document.referrer || null,
      ...params,
    });
  }
  catch (error) {
    console.error("Error logging page view:", error);
  }
};

const HOMEPAGE_FLAG_KEY = "homepage_view_logged";

export const logHomepageView = (params: Record<string, unknown> = {}): void => {
  if (sessionStorage.getItem(HOMEPAGE_FLAG_KEY) === "true") return;
  try {
    logEvent("homepage_view", params);
    sessionStorage.setItem(HOMEPAGE_FLAG_KEY, "true");
  }
  catch (error) {
    console.error("Error logging homepage view:", error);
  }
};

export const resetAnalyticsSessionFlags = (): void => {
  sessionStorage.removeItem(HOMEPAGE_FLAG_KEY);
};

export const logSearch = (params: Record<string, unknown> = {}): void => {
  try {
    logEvent("search_click", params);
  }
  catch (error) {
    console.error("Error logging search event:", error);
  }
};

export const logClickEvent = (eventName, params = {}) => {
  try {
    ReactGA.event(eventName, enrichParams({
      category: "homepage_click_event",
      ...params,
    }));
  }
  catch (error) {
    console.error("Error logging click event:", error);
  }
};

export const logGAEvent = (eventName: string, params: Record<string, unknown> = {}, category?: string) => {
  try {
    ReactGA.event(eventName, enrichParams({
      ...(category && { category }),
      ...params,
    }));
  }
  catch (error) {
    console.error(`Error logging GA event: ${eventName}`, error);
  }
};

export const logRecommendedPromptsActions = (category, eventName, params = {}) => {
  logGAEvent(eventName, params, category);
};

export const logDurationEvent = (eventName, durationSeconds, params = {}) => {
  try {
    ReactGA.event(eventName, enrichParams({
      category: "homepage_duration_event",
      duration_seconds: durationSeconds,
      ...params,
    }));
  }
  catch (error) {
    console.error("Error logging duration event:", error);
  }
};

export const logPurchaseEvent = (purchaseData) => {
  try {
    ReactGA.event("purchase", enrichParams(purchaseData));
  }
  catch (error) {
    console.error("Error logging purchase event:", error);
  }
};
